import Button from '@ben/orion/button';
import InputFeedback from '@ben/orion/input-feedback';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  IModularBenefitDetail,
  IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { memo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import type { StepsForm } from '../elements/types';
import { EMPTY_LIFE_EVENT_CONFIG } from './constants';
import LifeEventRootCard from './life-event-root-card';

interface LifeEventsSectionProps {
  stepsForm: StepsForm;
  errors: IModularBenefitMessages;
  successes: IModularBenefitMessages;
}

const LifeEventsSection = ({
  stepsForm,
  errors,
  successes,
}: LifeEventsSectionProps) => {
  const { control, setValue, formState } =
    useFormContext<IModularBenefitDetail>();
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const { fields, append, remove } = useFieldArray({
    name: 'lifeEventPolicies',
    control,
  });

  const stepsControl = stepsForm.control;
  const lifeEventsErrorMessage =
    typeof errors?.lifeEventPolicies === 'string'
      ? errors?.lifeEventPolicies
      : undefined;
  const lifeEventsSuccessMessage =
    !formState?.isDirty && typeof successes?.lifeEventPolicies === 'string'
      ? successes?.lifeEventPolicies
      : undefined;

  const addNewLifeEvent = () => {
    if (fields.length === 0) {
      setValue('lifeEventPolicies', []);
    }
    append(EMPTY_LIFE_EVENT_CONFIG);
    setExpandedCards((previous) => new Set(previous).add(fields.length));
  };

  const handleToggleCard = (index: number) => {
    setExpandedCards((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleRemoveCard = (index: number) => {
    remove(index);
    setExpandedCards((previous) => {
      const newSet = new Set(previous);
      newSet.delete(index);
      const adjustedSet = new Set<number>();
      for (const index_ of newSet) {
        if (index_ > index) {
          adjustedSet.add(index_ - 1);
        } else if (index_ < index) {
          adjustedSet.add(index_);
        }
      }
      return adjustedSet;
    });
    toast.info(
      'Life event removed successfully, but please save the benefit to reflect the changes.'
    );
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <Typography variant={TypographyVariant.h2}>Life events</Typography>
        <Button onClick={addNewLifeEvent}>Add life event</Button>
      </HeaderWrapper>
      {fields.length === 0 ? (
        <NoItemsWrapper>
          <Typography variant={TypographyVariant.bodyLg1}>
            No life events added yet
          </Typography>
        </NoItemsWrapper>
      ) : (
        <LifeEventsList>
          {fields.map((field, index) => (
            <LifeEventRootCard
              key={field.id}
              index={index}
              isExpanded={expandedCards.has(index)}
              onToggle={handleToggleCard}
              onRemove={handleRemoveCard}
              mainControl={stepsControl}
            />
          ))}
        </LifeEventsList>
      )}
      <InputFeedback
        errorMessage={lifeEventsErrorMessage}
        successMessage={lifeEventsSuccessMessage}
      />
    </Wrapper>
  );
};

export default memo(LifeEventsSection);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border-radius: 10px;
    border: 1px solid ${theme.palette.neutral[60]};
    gap: ${theme.spacing(2)};
  `}
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const NoItemsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props) => props.theme.spacing(4)};
`;

const LifeEventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(2)};
`;
